import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to track mouse position with throttling.
 * Returns normalized coordinates (0-1) relative to viewport.
 */
export const useMousePosition = (throttleMs = 33) => { // ~30fps
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const lastUpdate = useRef(0);
  const rafId = useRef(null);

  const updatePosition = useCallback((clientX, clientY) => {
    const now = Date.now();
    if (now - lastUpdate.current < throttleMs) return;

    lastUpdate.current = now;

    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }

    rafId.current = requestAnimationFrame(() => {
      setPosition({
        x: clientX / window.innerWidth,
        y: clientY / window.innerHeight
      });
    });
  }, [throttleMs]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      updatePosition(e.clientX, e.clientY);
    };

    const handleTouchMove = (e) => {
      if (e.touches[0]) {
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [updatePosition]);

  return position;
};

/**
 * Convert mouse position to eye pupil offset.
 * Constrains movement within eye socket bounds.
 */
export const useEyeTracking = (mousePosition, maxOffset = 4) => {
  const offsetX = (mousePosition.x - 0.5) * 2 * maxOffset;
  const offsetY = (mousePosition.y - 0.5) * 2 * maxOffset;

  return {
    x: offsetX * 0.8,  // Slightly dampen horizontal
    y: offsetY * 0.6   // More dampen vertical (natural eye movement)
  };
};
